
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RiderLayout } from "@/components/rider/RiderLayout";
import { MobileEventSelector } from "@/components/rider/MobileEventSelector";
import { DesktopEventSelector } from "@/components/rider/desktop/DesktopEventSelector";
import { LoadingEvents } from "@/components/rider/LoadingEvents";
import { getCommunityEvents } from "@/services/riderService";
import { CommunityEvent } from "@/types/rider";
import { Search, ArrowLeft } from "lucide-react";

const RiderEventSelect = () => {
  const [events, setEvents] = useState<CommunityEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();
  const [isImmersive, setIsImmersive] = useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const eventData = await getCommunityEvents();
        setEvents(eventData);
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch events:", error);
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const handleEventSelection = (eventId: string, index: number) => {
    setSelectedEventId(eventId);
    setActiveIndex(index);
  };

  const handleContinue = (selectedAddOns?: any[], totalPrice?: number) => {
    if (selectedEventId) {
      const params = new URLSearchParams({ eventId: selectedEventId });
      
      if (totalPrice) {
        params.append('price', totalPrice.toString());
      }
      
      if (selectedAddOns && selectedAddOns.length > 0) {
        params.append('addOns', JSON.stringify(selectedAddOns));
        
        // Create notes from selected add-ons
        const addOnNotes = selectedAddOns
          .map(addon => `${addon.description} (+$${addon.price})`)
          .join('\n');
        params.append('notes', addOnNotes);
      }
      
      navigate(`/rider-quote?${params.toString()}`);
    }
  };

  const handleManualEntry = () => {
    navigate('/rider-quote');
  };

  const handlePrevCard = () => {
    if (activeIndex > 0) {
      setActiveIndex(prev => prev - 1);
      setSelectedEventId(events[activeIndex - 1].id);
    }
  };

  const handleNextCard = () => {
    if (activeIndex < events.length - 1) {
      setActiveIndex(prev => prev + 1);
      setSelectedEventId(events[activeIndex + 1].id);
    }
  };

  // Mobile-specific navigation handlers that only change activeIndex
  const handleMobilePrevCard = () => {
    if (activeIndex > 0) {
      setActiveIndex(prev => prev - 1);
    }
  };

  const handleMobileNextCard = () => {
    if (activeIndex < events.length - 1) {
      setActiveIndex(prev => prev + 1);
    }
  };

  const handleCardTap = (eventId: string, index: number) => {
    if (!isImmersive) {
      setIsImmersive(true);
    }
    handleEventSelection(eventId, index);
  };

  return (
    <RiderLayout>
      <div className="min-h-screen bg-white transition-colors duration-300">
        {loading ? (
          <LoadingEvents />
        ) : (
          <>
            <MobileEventSelector
              events={events}
              selectedEventId={selectedEventId}
              activeIndex={activeIndex}
              onEventSelect={handleEventSelection}
              onContinue={handleContinue}
              onManualEntry={handleManualEntry}
              onPrevCard={handleMobilePrevCard}
              onNextCard={handleMobileNextCard}
            />

            <DesktopEventSelector
              events={events}
              activeIndex={activeIndex}
              selectedEventId={selectedEventId}
              isImmersive={isImmersive}
              setIsImmersive={setIsImmersive}
              handleEventSelection={handleEventSelection}
              handlePrevCard={handlePrevCard}
              handleNextCard={handleNextCard}
              handleCardTap={handleCardTap}
              handleContinue={handleContinue}
              handleManualEntry={handleManualEntry}
              handleResetSelection={() => {}}
            />
          </>
        )}
      </div>
    </RiderLayout>
  );
};

export default RiderEventSelect;
